<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import ImovelAPI from 'dashboard/api/imoveis';
import { useMapGetter } from 'dashboard/composables/store';

import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisCardSkeleton from 'dashboard/components-next/LaisCardSkeleton.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import ShareListingButton from 'dashboard/components-next/ShareListingButton.vue';
import SimilarListings from 'dashboard/components-next/SimilarListings.vue';
import UnitAmenities from 'dashboard/components-next/UnitAmenities.vue';
import ImageLightbox from 'dashboard/components-next/gallery/ImageLightbox.vue';
import PropertyCarousel from 'dashboard/components-next/gallery/PropertyCarousel.vue';
import {
  buildMediaTabs,
  groupMediaByType,
  MEDIA_TYPES,
} from 'dashboard/components-next/gallery/galleryUtils';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const isLoading = ref(true);
const error = ref('');
const imovel = ref(null);
const activeMediaIndex = ref(0);
const activeMediaTab = ref(MEDIA_TYPES.PHOTOS);
const isLightboxOpen = ref(false);

const catalog = computed(
  () => route.params.catalog?.toString() || 'properties'
);
const detailId = computed(() => route.params.id?.toString() || '');
const storageKey = computed(
  () =>
    `lais_catalog_detail:${accountId.value}:${catalog.value}:${detailId.value}`
);

const DETAIL_SECTIONS = [
  { id: 'resumo', label: 'Resumo', icon: 'i-lucide-layout-dashboard' },
  { id: 'fotos', label: 'Fotos', icon: 'i-lucide-images' },
  { id: 'valores', label: 'Valores', icon: 'i-lucide-badge-dollar-sign' },
  {
    id: 'caracteristicas',
    label: 'Características',
    icon: 'i-lucide-list-checks',
  },
  { id: 'endereco', label: 'Endereço', icon: 'i-lucide-map' },
];

const SECTION_ALIASES = {
  galeria: 'fotos',
  photos: 'fotos',
  mapa: 'endereco',
  map: 'endereco',
  identificacao: 'endereco',
  valores: 'valores',
  preco: 'valores',
  precos: 'valores',
  price: 'valores',
  detalhes: 'caracteristicas',
  caracteristicas: 'caracteristicas',
  data: 'caracteristicas',
  dados: 'caracteristicas',
};

const normalizeSection = value => {
  const section = value?.toString() || 'resumo';
  return SECTION_ALIASES[section] || section;
};

const activeSection = computed(() => normalizeSection(route.params.section));
const detailTabs = computed(() =>
  DETAIL_SECTIONS.map(section => ({
    ...section,
    active: activeSection.value === section.id,
  }))
);

const valueFrom = (item, ...keys) => {
  if (!item) return '';
  const attrs = item.additionalAttributes || item.additional_attributes || {};
  const match = keys.find(
    key =>
      (item[key] != null && item[key] !== '') ||
      (attrs[key] != null && attrs[key] !== '')
  );
  if (!match) return '';
  return item[match] != null && item[match] !== '' ? item[match] : attrs[match];
};

const codeFor = item =>
  valueFrom(
    item,
    'codigo',
    'code',
    'propertyCode',
    'property_code',
    'externalId',
    'external_id',
    'houseId',
    'house_id'
  ) || valueFrom(item, 'id');

const publicUrl = computed(() =>
  valueFrom(
    imovel.value,
    'linkExterno',
    'link_externo',
    'externalUrl',
    'external_url',
    'publicUrl',
    'public_url',
    'contactWebsite',
    'url'
  )
);

const title = computed(
  () =>
    valueFrom(imovel.value, 'titulo', 'title', 'name', 'address') ||
    'Detalhes do imóvel'
);

const location = computed(() =>
  [
    valueFrom(imovel.value, 'endereco', 'address', 'street'),
    valueFrom(imovel.value, 'bairro', 'neighborhood'),
    valueFrom(imovel.value, 'cidade', 'city'),
    valueFrom(imovel.value, 'uf', 'state'),
  ]
    .filter(Boolean)
    .join(', ')
);

const sourceLabel = computed(() => {
  if (catalog.value === 'empreendimentos') return 'Empreendimentos Imudei';
  if (catalog.value === 'portfolio') return 'Catálogo interno';
  return 'Base integrada de imóveis';
});

const mediaList = computed(() => {
  const media = valueFrom(imovel.value, 'midias', 'media', 'images', 'photos');
  return Array.isArray(media) ? media : [];
});
const mediaTabs = computed(() =>
  buildMediaTabs(groupMediaByType(mediaList.value))
);

const businessContext = computed(() => {
  const raw = String(
    valueFrom(
      imovel.value,
      'businessContext',
      'business_context',
      'finalidade',
      'transactionType'
    ) || ''
  ).toLowerCase();

  return ['purchase', 'sale', 'venda', 'comprar'].includes(raw)
    ? 'purchase'
    : 'rental';
});

const similarImovelId = computed(() => {
  const id = valueFrom(imovel.value, 'id');
  return /^\d+$/.test(String(id || '')) ? id : '';
});
const shouldShowUnitAmenities = computed(
  () =>
    catalog.value !== 'empreendimentos' &&
    ['resumo', 'caracteristicas'].includes(activeSection.value)
);
const shouldShowSimilarListings = computed(
  () =>
    Boolean(similarImovelId.value) &&
    catalog.value !== 'empreendimentos' &&
    ['resumo', 'caracteristicas'].includes(activeSection.value)
);

const formatBRL = value => {
  if (value == null || value === '') return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(numeric);
};

const humanize = key =>
  key
    .toString()
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

const printableValue = value => {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    return value
      .map(entry =>
        typeof entry === 'object' ? JSON.stringify(entry) : String(entry)
      )
      .join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const mainRows = computed(() => [
  ['Código', codeFor(imovel.value)],
  ['Fonte', sourceLabel.value],
  ['Status', valueFrom(imovel.value, 'status')],
  ['Tipo', valueFrom(imovel.value, 'tipoLabel', 'tipo', 'type')],
  ['Finalidade', valueFrom(imovel.value, 'finalidade', 'transactionType')],
  [
    'Valor de venda',
    formatBRL(
      valueFrom(
        imovel.value,
        'valorVenda',
        'valor_venda',
        'salePrice',
        'sale_price',
        'price'
      )
    ),
  ],
  [
    'Valor de aluguel',
    formatBRL(
      valueFrom(
        imovel.value,
        'valorAluguel',
        'valor_aluguel',
        'rentPrice',
        'rent_price'
      )
    ),
  ],
  [
    'Valor do condomínio',
    formatBRL(
      valueFrom(
        imovel.value,
        'valorCondominio',
        'valor_condominio',
        'condoPrice',
        'condo_price'
      )
    ),
  ],
  [
    'Valor total anual do IPTU',
    formatBRL(
      valueFrom(
        imovel.value,
        'valorIptu',
        'valor_iptu',
        'propertyTax',
        'property_tax'
      )
    ),
  ],
  ['Quartos', valueFrom(imovel.value, 'quartos', 'bedrooms')],
  ['Banheiros', valueFrom(imovel.value, 'banheiros', 'bathrooms')],
  [
    'Vagas',
    valueFrom(imovel.value, 'vagasGaragem', 'vagas_garagem', 'parking'),
  ],
  [
    'Área útil',
    valueFrom(
      imovel.value,
      'areaUtil',
      'area_util',
      'usableArea',
      'usable_area'
    ),
  ],
  [
    'Área total',
    valueFrom(
      imovel.value,
      'areaTotal',
      'area_total',
      'totalArea',
      'total_area'
    ),
  ],
  ['Endereço', valueFrom(imovel.value, 'endereco', 'address', 'street')],
  ['Bairro', valueFrom(imovel.value, 'bairro', 'neighborhood')],
  ['Cidade', valueFrom(imovel.value, 'cidade', 'city')],
  ['UF', valueFrom(imovel.value, 'uf', 'state')],
  ['CEP', valueFrom(imovel.value, 'cep', 'zipCode', 'zip_code')],
  ['Latitude', valueFrom(imovel.value, 'latitude', 'lat')],
  ['Longitude', valueFrom(imovel.value, 'longitude', 'lng')],
  ['Portal origem', valueFrom(imovel.value, 'portalOrigem', 'portal_origem')],
  [
    'Sincronizado em',
    valueFrom(
      imovel.value,
      'sincronizadoEm',
      'sincronizado_em',
      'lastSyncedAt',
      'last_synced_at'
    ),
  ],
]);

const VALUE_ROW_LABELS = [
  'Valor de venda',
  'Valor de aluguel',
  'Valor do condomínio',
  'Valor total anual do IPTU',
];
const ADDRESS_ROW_LABELS = [
  'Endereço',
  'Bairro',
  'Cidade',
  'UF',
  'CEP',
  'Latitude',
  'Longitude',
];
const CHARACTERISTIC_ROW_LABELS = [
  'Tipo',
  'Finalidade',
  'Quartos',
  'Banheiros',
  'Vagas',
  'Área útil',
  'Área total',
];
const HIDDEN_ADDITIONAL_KEYS = [
  'raw_payload',
  'rawPayload',
  'features',
  'torres',
  'tipologias',
  'unidades',
];

const additionalRows = computed(() => {
  const attrs =
    imovel.value?.additionalAttributes ||
    imovel.value?.additional_attributes ||
    {};
  return Object.entries(attrs)
    .filter(([key]) => !HIDDEN_ADDITIONAL_KEYS.includes(key))
    .map(([key, value]) => [humanize(key), value]);
});

const filteredMainRows = computed(() => {
  if (activeSection.value === 'valores') {
    return mainRows.value.filter(([label]) => VALUE_ROW_LABELS.includes(label));
  }

  if (activeSection.value === 'endereco') {
    return mainRows.value.filter(([label]) =>
      ADDRESS_ROW_LABELS.includes(label)
    );
  }

  if (activeSection.value === 'caracteristicas') {
    return mainRows.value.filter(([label]) =>
      CHARACTERISTIC_ROW_LABELS.includes(label)
    );
  }

  return mainRows.value;
});

const sectionTitle = computed(
  () =>
    ({
      valores: 'Valores',
      caracteristicas: 'Características',
      endereco: 'Endereço',
    })[activeSection.value] || 'Informações do imóvel'
);

const features = computed(
  () =>
    valueFrom(imovel.value, 'caracteristicas', 'features', 'amenities') || []
);
const shouldShowFeatures = computed(
  () =>
    features.value.length &&
    ['resumo', 'caracteristicas'].includes(activeSection.value)
);

const compactEntries = item =>
  Object.entries(item || {})
    .filter(([, value]) => value != null && value !== '')
    .slice(0, 10);

const detailCollections = computed(() =>
  [
    {
      title: t('PROPERTIES.CATALOG.TOWERS'),
      items: valueFrom(imovel.value, 'torres') || [],
      empty: t('PROPERTIES.CATALOG.EMPTY_TOWERS'),
    },
    {
      title: t('PROPERTIES.CATALOG.TYPES'),
      items: valueFrom(imovel.value, 'tipologias') || [],
      empty: t('PROPERTIES.CATALOG.EMPTY_TYPES'),
    },
    {
      title: t('PROPERTIES.CATALOG.UNITS'),
      items: valueFrom(imovel.value, 'unidades') || [],
      empty: t('PROPERTIES.CATALOG.EMPTY_UNITS'),
    },
  ].filter(
    collection => collection.items.length || catalog.value === 'empreendimentos'
  )
);

const visibleDetailCollections = computed(() =>
  ['resumo', 'caracteristicas'].includes(activeSection.value)
    ? detailCollections.value
    : []
);

const cacheItem = item => {
  if (typeof window === 'undefined' || !item) return;
  window.sessionStorage.setItem(storageKey.value, JSON.stringify(item));
};

const loadFromCache = () => {
  if (typeof window === 'undefined') return null;
  const cached = window.sessionStorage.getItem(storageKey.value);
  if (!cached) return null;
  try {
    return JSON.parse(cached);
  } catch {
    return null;
  }
};

const findMatch = items =>
  items.find(item => {
    const code = String(codeFor(item) || '');
    const id = String(valueFrom(item, 'id') || '');
    const externalId = String(
      valueFrom(item, 'externalId', 'external_id') || ''
    );
    return [code, id, externalId].includes(detailId.value);
  }) || items[0];

const fetchDetail = async () => {
  isLoading.value = true;
  error.value = '';

  const cached = loadFromCache();
  if (cached) imovel.value = camelcaseKeys(cached, { deep: true });

  try {
    if (catalog.value === 'portfolio') {
      const { data } = await ImovelAPI.show(detailId.value);
      imovel.value = camelcaseKeys(data.payload || data, { deep: true });
      cacheItem(imovel.value);
      return;
    }

    if (catalog.value === 'empreendimentos') {
      const { data } = await ImovelAPI.empreendimentoDetail(detailId.value);
      imovel.value = camelcaseKeys(data.payload || data, { deep: true });
      cacheItem(imovel.value);
      return;
    }

    const fetcher =
      catalog.value === 'empreendimentos'
        ? ImovelAPI.empreendimentos
        : ImovelAPI.externalProperties;
    const { data } = await fetcher.call(ImovelAPI, {
      q: detailId.value,
      page: 1,
      per_page: 50,
    });
    const match = findMatch(data.payload || []);
    if (match) {
      imovel.value = camelcaseKeys(match, { deep: true });
      cacheItem(imovel.value);
    } else if (!imovel.value) {
      error.value = t('PROPERTIES.CATALOG.NOT_FOUND');
    }
  } catch {
    if (!imovel.value) {
      error.value = t('PROPERTIES.CATALOG.LOAD_ERROR');
    }
  } finally {
    isLoading.value = false;
  }
};

const openPublicUrl = () => {
  if (publicUrl.value && typeof window !== 'undefined') {
    window.open(publicUrl.value, '_blank', 'noopener');
  }
};

const openLightbox = ({ index = 0, item = {} } = {}) => {
  activeMediaIndex.value = index;
  activeMediaTab.value = item.type || MEDIA_TYPES.PHOTOS;
  isLightboxOpen.value = true;
};

const handleLightboxChange = ({ index, tab }) => {
  activeMediaIndex.value = index;
  activeMediaTab.value = tab || activeMediaTab.value;
};

const goToSection = section => {
  const routeName =
    section === 'resumo' ? 'imovel_catalog_show' : 'imovel_catalog_section';
  router.push({
    name: routeName,
    params: {
      accountId: route.params.accountId || accountId.value,
      catalog: catalog.value,
      id: detailId.value,
      ...(section === 'resumo' ? {} : { section }),
    },
    query: route.query,
  });
};

onMounted(fetchDetail);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="flex h-full flex-col overflow-hidden bg-lais-bg-page font-redHatText"
  >
    <LaisHeader
      :title="title"
      :helper="location || sourceLabel"
      :breadcrumb="[
        {
          label: catalog === 'empreendimentos' ? 'Empreendimentos' : 'Imóveis',
          to: { name: 'imoveis_index', query: { catalog } },
        },
        { label: 'Detalhes' },
      ]"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-arrow-left"
          @click="router.back()"
        >
          Voltar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="publicUrl"
          variant="ghost"
          icon="i-lucide-globe-2"
          @click="openPublicUrl"
        >
          Abrir link
        </LaisPrimaryButton>
        <ShareListingButton
          v-if="publicUrl"
          :url="publicUrl"
          :title="title"
          :business-context="businessContext"
          source="catalog_detail"
          variant="text"
        />
      </template>
    </LaisHeader>

    <div
      v-if="!isLoading && imovel"
      class="border-b border-lais-border-soft bg-white px-4 py-3 sm:px-6 lg:px-10"
    >
      <div class="flex flex-wrap gap-2">
        <button
          v-for="section in detailTabs"
          :key="section.id"
          type="button"
          class="inline-flex min-h-10 items-center gap-2 rounded-lais-sm border px-3 py-2 text-[13px] font-semibold transition-colors"
          :class="
            section.active
              ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
              : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
          "
          @click="goToSection(section.id)"
        >
          <span class="size-4" :class="section.icon" aria-hidden="true" />
          {{ section.label }}
        </button>
      </div>
    </div>

    <main class="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-10">
      <div
        v-if="isLoading && !imovel"
        class="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]"
      >
        <LaisCardSkeleton :count="1" mode="card" />
        <LaisCardSkeleton :count="2" mode="compact" />
      </div>
      <div
        v-else-if="error && !imovel"
        class="rounded-lais border border-lais-border bg-white p-8 text-center text-[13px] text-lais-text-muted"
      >
        {{ error }}
      </div>
      <div v-else class="grid gap-5">
        <section
          v-if="['resumo', 'fotos'].includes(activeSection)"
          class="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]"
        >
          <div
            class="overflow-hidden rounded-lais border border-lais-border bg-white"
          >
            <PropertyCarousel
              v-model="activeMediaIndex"
              :items="mediaList"
              :media-tabs="mediaTabs"
              empty-message="Sem mídia disponível para este catálogo"
              @open="openLightbox"
            />
          </div>
          <LaisCard v-if="activeSection === 'resumo'" title="Resumo">
            <div class="grid gap-3 text-[13px]">
              <div
                v-for="[label, value] in mainRows.slice(0, 10)"
                :key="label"
                class="flex items-start justify-between gap-4 border-b border-lais-border-soft pb-2 last:border-b-0"
              >
                <span class="font-semibold text-lais-text-body">{{
                  label
                }}</span>
                <span class="max-w-[60%] text-right text-lais-text">{{
                  printableValue(value)
                }}</span>
              </div>
            </div>
          </LaisCard>
        </section>

        <LaisCard v-if="activeSection !== 'fotos'" :title="sectionTitle">
          <div class="grid gap-3 md:grid-cols-2">
            <div
              v-for="[label, value] in filteredMainRows"
              :key="label"
              class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                {{ label }}
              </p>
              <p class="mt-1 break-words text-[13px] text-lais-text">
                {{ printableValue(value) }}
              </p>
            </div>
          </div>
        </LaisCard>

        <LaisCard v-if="shouldShowUnitAmenities" title="Amenidades">
          <UnitAmenities :imovel="imovel" />
        </LaisCard>

        <LaisCard v-if="shouldShowFeatures" title="Características">
          <div class="flex flex-wrap gap-2">
            <span
              v-for="feature in features"
              :key="printableValue(feature)"
              class="rounded-full bg-lais-primary-50 px-3 py-1 text-[12px] font-semibold text-lais-primary"
            >
              {{ printableValue(feature) }}
            </span>
          </div>
        </LaisCard>

        <LaisCard
          v-for="collection in visibleDetailCollections"
          :key="collection.title"
          :title="collection.title"
        >
          <div
            v-if="collection.items.length"
            class="grid max-h-[360px] gap-3 overflow-auto"
          >
            <article
              v-for="(item, index) in collection.items"
              :key="`${collection.title}-${item.id || item.externalId || index}`"
              class="rounded-lais-sm border border-lais-border-soft bg-white p-3"
            >
              <div class="mb-2 flex items-center justify-between gap-3">
                <p class="font-semibold text-lais-text">
                  {{
                    item.name ||
                    item.reference ||
                    item.unitNumber ||
                    item.id ||
                    `${collection.title} ${index + 1}`
                  }}
                </p>
                <span
                  v-if="item.status"
                  class="rounded-full bg-lais-bg-soft px-2 py-0.5 text-[11px] font-semibold text-lais-text-body"
                >
                  {{ item.status }}
                </span>
              </div>
              <div class="grid gap-2 md:grid-cols-2">
                <div
                  v-for="[key, value] in compactEntries(item)"
                  :key="`${collection.title}-${index}-${key}`"
                  class="min-w-0 rounded-lais-sm bg-lais-bg-soft px-2 py-1.5"
                >
                  <p
                    class="text-[10px] font-semibold uppercase text-lais-text-muted"
                  >
                    {{ humanize(key) }}
                  </p>
                  <p class="break-words text-[12px] text-lais-text">
                    {{ printableValue(value) }}
                  </p>
                </div>
              </div>
            </article>
          </div>
          <p v-else class="text-[13px] text-lais-text-muted">
            {{ collection.empty }}
          </p>
        </LaisCard>

        <LaisCard
          v-if="['resumo', 'caracteristicas'].includes(activeSection)"
          title="Atributos adicionais"
        >
          <div v-if="additionalRows.length" class="grid gap-3 md:grid-cols-2">
            <div
              v-for="[label, value] in additionalRows"
              :key="label"
              class="rounded-lais-sm border border-lais-border-soft bg-white px-3 py-2"
            >
              <p
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                {{ label }}
              </p>
              <p class="mt-1 break-words text-[13px] text-lais-text">
                {{ printableValue(value) }}
              </p>
            </div>
          </div>
          <p v-else class="text-[13px] text-lais-text-muted">
            {{ t('PROPERTIES.CATALOG.NO_EXTRA_ATTRIBUTES') }}
          </p>
        </LaisCard>

        <SimilarListings
          v-if="shouldShowSimilarListings"
          :imovel-id="similarImovelId"
          :account-id="accountId"
          kind="neighbourhood"
        />
      </div>
    </main>

    <ImageLightbox
      v-model:open="isLightboxOpen"
      :items="mediaList"
      :tabs="mediaTabs"
      :initial-index="activeMediaIndex"
      :initial-tab="activeMediaTab"
      :title="title"
      @change="handleLightboxChange"
    />
  </section>
</template>
